import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, FileUp, Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { farmGet, farmHeaders, farmPost } from "./shared";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI = [
  { id: "contratto", label: "Contratto di servizio" },
  { id: "nomina_trattamento_dati", label: "Privacy (nomina trattamento dati)" },
];

export default function ModelliContrattiPrivacy() {
  const { token } = useFarmaciaAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await farmGet("/farmacia/modelli-documenti", token);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento modelli");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const byTipo = (tipo) => items.find((i) => i.tipo === tipo);

  const upload = async (tipo, file) => {
    if (!file) return;
    setBusy(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await farmPost(`/farmacia/modelli-documenti/${encodeURIComponent(tipo)}`, token, fd, true);
      toast.success("Modello caricato: le strutture collegate potranno scaricarlo");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setBusy(null);
    }
  };

  const download = async (tipo, fileName) => {
    setBusy(`dl-${tipo}`);
    try {
      const res = await axios.get(
        `${API_URL}/api/farmacia/modelli-documenti/${encodeURIComponent(tipo)}/download`,
        { headers: farmHeaders(token), responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `modello_${tipo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fallito");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Modelli Contratto e Privacy</h2>
          <p className="mt-1 text-xs text-slate-500">
            Carica i modelli blank (DOCX/PDF) che le strutture scaricano da Deblistering → Impostazioni,
            firmano fuori Affidya e ricaricano come documenti firmati condivisi.
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-lg" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Aggiorna
        </Button>
      </div>

      {loading && !items.length ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {TIPI.map(({ id, label }) => {
            const mod = byTipo(id);
            const ok = mod && !mod.mancante && mod.id;
            return (
              <div key={id} className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-slate-900">{label}</p>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${ok ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}`}
                  >
                    {ok ? "Disponibile" : "Mancante"}
                  </Badge>
                </div>
                {ok ? (
                  <p className="mt-2 truncate text-xs text-slate-600">
                    {mod.file_name}
                    {mod.updated_at ? ` · ${String(mod.updated_at).slice(0, 10)}` : ""}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">Nessun modello caricato</p>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {ok ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      disabled={busy === `dl-${id}`}
                      onClick={() => download(id, mod.file_name)}
                    >
                      <Download className="mr-1 h-3.5 w-3.5" />
                      Scarica
                    </Button>
                  ) : null}
                  <label className="inline-flex cursor-pointer">
                    <Button
                      size="sm"
                      className="rounded-lg bg-[#4CAF50] hover:bg-[#43A047]"
                      asChild
                      disabled={busy === id}
                    >
                      <span>
                        {busy === id ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : ok ? (
                          <Upload className="mr-1 h-3.5 w-3.5" />
                        ) : (
                          <FileUp className="mr-1 h-3.5 w-3.5" />
                        )}
                        {ok ? "Sostituisci" : "Carica modello"}
                      </span>
                    </Button>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      disabled={busy === id}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) upload(id, f);
                      }}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
